import { PopularityTrendGapKeyword } from "@apiHooks/useCategoryProfilePopularityTrendGap";
import {
    ARROW_MARGIN,
    DOT_RADIUS,
    QuadrantKey,
    TOOLTIP_EDGE_PAD,
    TOOLTIP_GAP,
    ViewConfig,
} from "./PopularityTrendGapChart.constants";

export interface PlottableKeyword {
    wordId:   number | undefined;
    wordName: string;
    x:        number; // raw metric value for xKey
    y:        number; // raw metric value for yKey
}

/**
 * Filter keywords to those that have BOTH active-view metrics defined.
 * Keywords missing either metric are skipped for that view — not coerced to 0.
 */
export function getPlottableKeywords(
    keywords: PopularityTrendGapKeyword[],
    config: ViewConfig,
): PlottableKeyword[] {
    return keywords.flatMap((kw) => {
        const x = kw[config.xKey];
        const y = kw[config.yKey];
        if (typeof x !== 'number' || typeof y !== 'number') return [];
        return [{
            wordId:   kw.wordId,
            wordName: kw.wordName ?? '',
            x,
            y,
        }];
    });
}

/** Map a value in [min, max] to a pixel coordinate within [padding, size - padding]. */
export function toPixel(value: number, min: number, max: number, size: number, padding: number): number {
    if (max === min) return size / 2;
    return padding + ((value - min) / (max - min)) * (size - 2 * padding);
}

/**
 * Determine the quadrant key for a dot based on its pixel position vs the plot center.
 * The center pixel is always the midpoint of the plot area.
 */
export function getQuadrantKey(
    cx: number,
    cy: number,
    plotWidth: number,
    plotHeight: number,
): QuadrantKey {
    const midX = plotWidth / 2;
    const midY = plotHeight / 2;
    // SVG Y increases downward, so cy < midY means visually "top".
    const isTop   = cy <  midY;
    const isLeft  = cx <  midX;
    if (isTop  && isLeft)  return 'topLeft';
    if (isTop  && !isLeft) return 'topRight';
    if (!isTop && isLeft)  return 'bottomLeft';
    return 'bottomRight';
}

export interface TooltipPositionInput {
    cx:         number;
    cy:         number;
    tipW:       number;
    tipH:       number;
    containerW: number;
    containerH: number;
}

export interface TooltipPosition {
    left:      number;
    top:       number;
    /** Which side of the dot the tooltip box sits on: 'top' = box is above the dot
     *  (caret points down from the box's bottom edge); 'bottom' = box is below the dot
     *  (caret points up from the box's top edge). */
    placement: 'top' | 'bottom';
    /** Caret's horizontal offset from the box's left edge, so it points at the dot.
     *  Clamped away from the rounded corners by ARROW_MARGIN on both sides. */
    arrowLeft: number;
}

/**
 * Compute a dot-anchored tooltip position, clamped fully inside the plot container.
 * Pure function (no DOM reads) so it stays testable even where getBoundingClientRect
 * cannot be trusted (e.g. jsdom, which returns zeros for real layout).
 *
 * Prefers placing the tooltip above the dot; flips below when there isn't room above.
 * Horizontally centers the tooltip on the dot, then clamps both axes so the box never
 * crosses the container edges. Also returns where the box sits relative to the dot
 * (`placement`) and where the pointer caret should sit on the box (`arrowLeft`), so a
 * caller can render a caret that still points at the dot after the box is edge-clamped.
 */
export function computeTooltipPosition({
    cx,
    cy,
    tipW,
    tipH,
    containerW,
    containerH,
}: TooltipPositionInput): TooltipPosition {
    // Leave DOT_RADIUS + TOOLTIP_GAP between the dot's edge and the box, mirroring the
    // below-placement branch, so the down-caret tip clears the dot by the same visible gap.
    const aboveTop = cy - DOT_RADIUS - TOOLTIP_GAP - tipH;
    const placement: 'top' | 'bottom' = aboveTop >= 0 ? 'top' : 'bottom';
    let top = placement === 'top' ? aboveTop : cy + DOT_RADIUS + TOOLTIP_GAP;
    top = Math.min(Math.max(top, 0), Math.max(0, containerH - tipH));

    let left = cx - tipW / 2;
    left = Math.min(
        Math.max(left, TOOLTIP_EDGE_PAD),
        Math.max(TOOLTIP_EDGE_PAD, containerW - tipW - TOOLTIP_EDGE_PAD),
    );

    // Degenerate case: a box narrower than 2*ARROW_MARGIN has no room to keep the caret
    // off both corners at once — collapse the clamp range to the box's center instead of
    // producing an inverted (lower > upper) range or a negative offset.
    const arrowLower = Math.min(ARROW_MARGIN, tipW / 2);
    const arrowUpper = Math.max(arrowLower, tipW - ARROW_MARGIN);
    const arrowLeft = Math.min(Math.max(cx - left, arrowLower), arrowUpper);

    return { left, top, placement, arrowLeft };
}

/**
 * Format a metric value as a percentage string.
 * [ASSUMPTION] The API returns penetration/loveLikeIt/knowIt/fourYearGrowth as 0–100 scale doubles
 * (confirmed by MenuPenetrationChart which does Math.round(value) + "%" with no ×100 multiply).
 * If the scale turns out to be 0–1, multiply by 100 before calling this.
 */
export function formatMetricPercent(value: number): string {
    return `${Math.round(value)}%`;
}
